import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Product } from "@/types/product";
import { generateSlug } from "@/lib/utils";
import React from "react";

/** Map a Supabase product row → frontend Product type */
const mapRow = (r: any): Product => ({
  id: r.id,
  name: r.name,
  brand: r.brand,
  model: r.model ?? "",
  category: r.category,
  processor: r.processor ?? "",
  ram: r.ram ?? "",
  storage: r.storage ?? "",
  screenSize: r.screen_size ?? "",
  condition: r.condition ?? "Grade A",
  warranty: r.warranty ?? "6 Months",
  price: Number(r.price),
  originalPrice: Number(r.original_price),
  gstRate: Number(r.gst_rate),
  stock: Number(r.stock),
  images: r.images?.length ? r.images : ["/placeholder.svg"],
  description: r.description ?? "",
  featured: r.featured ?? false,
  has_variants: r.has_variants ?? false,
});

/**
 * Fetch published, in-stock products from the database.
 * Automatically refetches when React Query cache is invalidated.
 */
export function useProducts() {
  return useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("status", "published")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("[useProducts] fetch error:", error);
        throw error;
      }

      console.log(`[useProducts] fetched ${data?.length ?? 0} products from database`);
      return (data ?? []).map(mapRow);
    },
    /* TTFB/caching: keep data fresh for 30s to avoid redundant network fetches.
       Realtime subscription will invalidate cache when products change. */
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Subscribe to realtime product changes — call once at app level */
export function useProductsRealtime() {
  const qc = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("products-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "products" },
        () => {
          qc.invalidateQueries({ queryKey: ["products"] });
          qc.invalidateQueries({ queryKey: ["product"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);
}

/**
 * Fetch a single product by ID (any status, for detail page).
 */
export function useProduct(id: string | undefined) {
  return useQuery<Product | null>({
    queryKey: ["product", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("id", id!)
        .maybeSingle();

      if (error) {
        console.error("[useProduct] fetch error:", error);
        throw error;
      }
      if (!data) return null;
      return mapRow(data);
    },
  });
}

/**
 * Fetch a single product by matching its generated SEO slug.
 * Used by the ProductDetail page to resolve beautiful URLs.
 * Fallbacks to checking if the parameter is actually a UUID to maintain backward compatibility.
 */
export function useProductBySlug(slugOrId: string | undefined) {
  const qc = useQueryClient();
  const isUuid = slugOrId ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slugOrId) : false;

  return useQuery<Product | null>({
    queryKey: ["product", slugOrId],
    enabled: !!slugOrId,
    queryFn: async () => {
      // 1. Try resolving using UUID directly
      if (isUuid) {
        const { data, error } = await supabase
          .from("products")
          .select("*")
          .eq("id", slugOrId!)
          .maybeSingle();
        if (error) throw error;
        return data ? mapRow(data) : null;
      }

      // 2. Try to find the product in the local React Query cache FIRST (Performance boost)
      const cachedProducts = qc.getQueryData<Product[]>(["products"]);
      if (cachedProducts && cachedProducts.length > 0) {
        const match = cachedProducts.find((p) => generateSlug(p.name) === slugOrId);
        if (match) {
          console.log(`[useProductBySlug] Resolved ${slugOrId} from local cache`);
          return match;
        }
      }

      // 3. Fallback: Fetch all products and match by generated slug if not in cache
      console.log(`[useProductBySlug] Cache miss for ${slugOrId}, fetching from DB...`);
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("is_active", true);
      if (error) throw error;

      const match = (data ?? []).find(
        (r) => generateSlug(r.name) === slugOrId
      );
      return match ? mapRow(match) : null;
    },
    staleTime: 30_000,
  });
}
